/* eslint-disable max-lines */
import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { setCommentRange } from 'typescript';
import { CloudServiceProvider } from '../../../constants/AppConstants';
import { Toast } from '../../../components/notifications/Toast/Toast';
import { NotificationTypes } from '../../../components/notifications/Toast/ToastConstants';
import {
  IAllEnvironmentsRespObj,
  ICreateEnvironmentPayload,
  IDownloadArchitectureDiagramPayload,
  IFetchCategoryAndTechDomainResponse,
  IGetEnvironmentsQueryParams,
  IGetWorkloadQueryParams,
  ILandingZoneResponse,
  ILandingZoneSector,
  IUploadArchitecturePayload,
  IWorkloadPatternRespObj,
  LandingZoneStatus,
  LzMicroserviceStatus,
} from '../../models/BuildDeployModels';
import {
  ICheckLandingZoneStatus,
  ICheckMicroServicesPatternStatus,
  IConfigureWorkLoadsPattern,
} from '../../models/DashboardModels';
import BuildDeployService from '../../services/BuildDeployService/BuildDeployService';
import {
  BuildDeployAction,
  BuildDeployActions,
  BuildDeployActionTypes,
  ILandingZoneErrorResponse,
} from '../actions/BuildDeployActions';
import { ToastActions } from '../actions/ToastActions';
import { IApplicationState } from '../reducers';
import {
  IArchitectureCataloguePayload,
  IArchitectureCatalogueResponse,
  ICloudLandingZoneKeysPayload,
  ICloudLandingZoneKeysResponse,
  IConfigureDeployCloudPayload,
  IConfigureDeployCloudThroughConsolePayload,
  IConfigureLandingZonePayload,
  IConfigureLandingZoneRequestPayload,
  IConfigureLZLoadingPayload,
  ICreateYamlPayload,
  IDeleteLandingZonePayload,
  IDeleteWorkLoadPatternPayload,
  IDeployActivityHistory,
  IDeploymentLogsPayload,
  IDeploymentLogsResponse,
  IDeploymentsResponse,
  IEnvironmentsDeployed,
  ILandingZoneDeploymentResponse,
  ILandingZoneDeploymentStatus,
  ILandingZoneState,
  IPipelinesAnalyticsResponse,
  IPipelinesCummulativeAnalyticsResponse,
  IUpdateLandingZoneDeployDetails,
  IYamlContent,
  IFormInterface,
  IIndustryYamlPayload,
  IGetKeysRequestBody,
  IUploadPayload,
  IIndustryPolicy,
  IIndustryCompliance,
  IIndustryPolicyRequest,
  IGenAiCreatePayload,
  IGenAiCreateResponse,
  IGenAiCreateDownloadPayload,
  IWorkFLowLogsResponse,
  IValidateLandingZonePayload,
  IUploadCustomCloudConfigurationFilePayload,
  IUploadCustomCloudConfigurationFileResponse,
  ICloudBuildTfVarsPayload,
  ITemporaryCloudBuildConfigVariableResponse,
  IValidateCustomConfigurationPayload,
  IGenerateArchitectureDiagramsPayload,
  IGenerateArchitectureDiagramsResponse,
  IUploadArchitectureDiagramsPayload,
  IUploadArchitectureDiagramsResponse,
  IUploadArchitectureDiagramsPayloadWithFile,
  IValidateCustomConfigurationResponse,
  IReviseArchitectureDiagramsPayload,
  IConvertArchitectureDiagramsPayload,
} from '../state/BuildDeployState';

export function* getLandingZonesWorker(action: BuildDeployAction) {
  try {
    //API call to get all the Cloud foundations
    const projectId = action.data as string;

    const landingZoneList: Array<ILandingZoneResponse> =
      yield BuildDeployService.getLandingZoneDetails(projectId);

    const landingZones: Array<ILandingZoneState> = landingZoneList.map(
      ({
        LandingZoneName,
        CurrentStatus,
        CloudServiceProvider,
        LandingZoneId,
        Sector,
        GCPProjectId,
        MicroserviceInfo,
        Errors,
        IAC,
        CreatedOn,
        industryComplianceId,
        industryComplianceName,
        complianceStandardList,
      }) => {
        const isEnabled = CurrentStatus === LandingZoneStatus.CREATED;
        const microServicesConfigured = MicroserviceInfo?.status === LzMicroserviceStatus.CREATED;
        const microServicesError = MicroserviceInfo?.status === LzMicroserviceStatus.ERROR;
        return {
          landingZoneId: LandingZoneId,
          landingZoneEnabled: isEnabled,
          landingZoneConfigured: isEnabled,
          landingZoneName: LandingZoneName,
          projectName: GCPProjectId,
          loadingLandingZone: !isEnabled,
          loadingEtlWorkload: false,
          sector: Sector,
          loadingMicroServicesWorkload: Boolean(
            isEnabled && MicroserviceInfo?.ids && !microServicesConfigured && !microServicesError,
          ),
          workLoadPatternMicroServicesEnabled: microServicesConfigured,
          workLoadPatternEtlEnabled: false,
          workLoadPatternMicroServicesConfigured: microServicesConfigured,
          workLoadPatternMicroServicesError: microServicesError,
          workLoadPatternEtlConfigured: false,
          cloudServiceProvider: CloudServiceProvider,
          workLoadPatternMicroServicesId: MicroserviceInfo?.ids,
          landingZoneStatus: CurrentStatus,
          errorMessage: Errors,
          IAC,
          createdOn: CreatedOn,
          industryComplianceId,
          industryComplianceName,
          complianceStandardList,
        };
      },
    );

    yield put(BuildDeployActions.getBuildDeployDetailsSuccess(landingZones));
    // Comment out if there are any dummy In Progress LZs
    // for (const { landingZoneName, landingZoneId } of landingZones.filter(
    //   (landingZone) => landingZone.loadingLandingZone,
    // )) {
    //   yield put(
    //     BuildDeployActions.checkLandingZoneStatus({
    //       landingZoneId: landingZoneId,
    //       landingZoneName: landingZoneName,
    //     }),
    //   );
    // }

    for (const { landingZoneName, workLoadPatternMicroServicesId } of landingZones.filter(
      (landingZone) => landingZone.loadingMicroServicesWorkload,
    )) {
      if (workLoadPatternMicroServicesId)
        yield put(
          BuildDeployActions.checkMicroServicesPatternStatus({
            microServiceId: workLoadPatternMicroServicesId,
            landingZoneName: landingZoneName,
          }),
        );
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getBuildDeployDetailsError(error));
    }
  }
}

export function* destroyLandingZoneWorker(action: BuildDeployAction) {
  try {
    //API call to destroy Cloud foundation
    const payload = action.data as IDeleteLandingZonePayload;
    const { landingZoneName, landingZoneId, selectedProjectId } = payload;
    const response: string = yield BuildDeployService.destroyLaningZone(payload);
    yield put(BuildDeployActions.deleteLandingZoneSuccess(landingZoneId));
    yield put(BuildDeployActions.getBuildDeployDetailStarted(selectedProjectId));
    yield put(
      ToastActions.showToast({
        description: `${landingZoneName || 'Cloud Environment'} deleted successfully`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.deleteLandingZoneError(error));
    }
  }
}

export function* destroyWorkLoadPatternWorker(action: BuildDeployAction) {
  try {
    //API call to destroy Cloud foundation
    const payload = action.data as IDeleteWorkLoadPatternPayload;
    const { landingZoneId, landingZoneName } = payload;
    const response: string = yield BuildDeployService.destroyWorkLoadPattern(payload);
    yield put(BuildDeployActions.destroyWorkLoadPatternSuccess(landingZoneId));
    yield put(
      ToastActions.showToast({
        description: `WorkLoad Pattern For Microservices - ${
          landingZoneName || 'Cloud Environment'
        } deleted Successfully`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.deleteLandingZoneError(error));
    }
  }
}

export function* getWorkLoadPatternWorker(action: BuildDeployAction) {
  try {
    //API call to destroy Cloud foundation
    const response: Array<IWorkloadPatternRespObj> = yield BuildDeployService.getWorkloads(
      action.data as IGetWorkloadQueryParams,
    );
    yield put(BuildDeployActions.getAllAvailableWorkLoadPatternSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getAllAvailableWorkLoadPatternError(error));
    }
  }
}
export function* getAllEnvironmentsWorker(action: BuildDeployAction) {
  try {
    //API call to destroy Cloud foundation
    const response: Array<IAllEnvironmentsRespObj> = yield BuildDeployService.getEnvironments(
      action.data as IGetEnvironmentsQueryParams,
    );
    yield put(BuildDeployActions.getAllEnvironmentsSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getAllEnvironmentsError(error));
    }
  }
}

export function* enableLandingZoneWorker(action: BuildDeployAction) {
  try {
    // API call to enable Cloud foundation
    yield put(
      ToastActions.showToast({
        description: 'Cloud Environment enabled',
        type: NotificationTypes.SUCCESS,
      }),
    );
    yield put(BuildDeployActions.enableLandingZoneSuccess(action.data as number));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.enableLandingZoneError(error));
    }
  }
}

export function* enableMicroServicesWorkload(action: BuildDeployAction) {
  try {
    // API call to enable Microservices zone
    yield put(BuildDeployActions.enableMicroServicesPatternSuccess(action.data as number));

    const buildDeployLZ = ({ buildDeploy: { landingZones } }: IApplicationState) => landingZones;
    const landingZones: ILandingZoneState[] = yield select(buildDeployLZ);
    const microServiceEnableState =
      landingZones[action.data as number].workLoadPatternMicroServicesEnabled;
    const msConfigured = landingZones[action.data as number].workLoadPatternMicroServicesConfigured;
    if (!msConfigured) {
      yield put(
        ToastActions.showToast({
          description: `Microservices Workload ${microServiceEnableState ? 'enabled' : 'disabled'}`,
          type: NotificationTypes.SUCCESS,
        }),
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.enableMicroServicesPatternError(error));
    }
  }
}

export function* enableEtlWorkLoadWorker(action: BuildDeployAction) {
  try {
    // API call to enable Microservices zone
    yield put(BuildDeployActions.enableEtlPatternSuccess(action.data as number));
    const buildDeployLZ = ({ buildDeploy: { landingZones } }: IApplicationState) => landingZones;
    const landingZones: ILandingZoneState[] = yield select(buildDeployLZ);
    const ETLEnableState = landingZones[action.data as number].workLoadPatternEtlEnabled;
    yield put(
      ToastActions.showToast({
        description: `ETL Workload ${ETLEnableState ? 'enabled' : 'disabled'}`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.enableEtlPatterError(error));
    }
  }
}

export function* configureLandingZoneWorker(action: BuildDeployAction) {
  try {
    // API call to configure Cloud foundation
    const { index, landingZoneName, uploadData, providerName, apiEndpoint, projectId } =
      action.data as IConfigureLandingZonePayload;
    const requestPayload = { index, landingZoneName, uploadData, providerName, apiEndpoint };
    const data = JSON.parse(uploadData?.userdata.content);
    const response: ILandingZoneResponse = yield BuildDeployService.startUploadFunc(requestPayload);
    const {
      LandingZoneName,
      CurrentStatus,
      CloudServiceProvider: cloudServiceProvider,
      LandingZoneId,
      Sector: sector,
      GCPProjectId,
      MicroserviceInfo,
      Errors,
      IAC,
      CreatedOn,
      industryComplianceId,
      industryComplianceName,
      complianceStandardList,
      AccountId,
      ProjectId,
    } = response;
    const isEnabled = CurrentStatus === LandingZoneStatus.CREATED;
    const microServicesConfigured = MicroserviceInfo?.status === LzMicroserviceStatus.CREATED;
    const microServicesError = MicroserviceInfo?.status === LzMicroserviceStatus.ERROR;
    const landingZoneObj: ILandingZoneState = {
      landingZoneId: LandingZoneId,
      landingZoneEnabled: isEnabled,
      landingZoneConfigured: isEnabled,
      landingZoneName: LandingZoneName,
      projectName: GCPProjectId,
      loadingLandingZone: !isEnabled,
      loadingEtlWorkload: false,
      sector: sector,
      loadingMicroServicesWorkload: Boolean(
        isEnabled && MicroserviceInfo?.ids && !microServicesConfigured && !microServicesError,
      ),
      workLoadPatternMicroServicesEnabled: microServicesConfigured,
      workLoadPatternEtlEnabled: false,
      workLoadPatternMicroServicesConfigured: microServicesConfigured,
      workLoadPatternMicroServicesError: microServicesError,
      workLoadPatternEtlConfigured: false,
      cloudServiceProvider: cloudServiceProvider,
      workLoadPatternMicroServicesId: MicroserviceInfo?.ids,
      landingZoneStatus: CurrentStatus,
      errorMessage: Errors,
      IAC,
      createdOn: CreatedOn,
      accountId: AccountId,
      projectId: ProjectId,
      industryComplianceId,
      industryComplianceName,
      complianceStandardList,
    };
    const loadingContent: IConfigureLZLoadingPayload = {
      csp: providerName as CloudServiceProvider,
      landingZoneObj: landingZoneObj,
      index: 0,
      landingZoneName: landingZoneName,
      sector: data.sector,
    };
    yield put(BuildDeployActions.configureLandingZoneLoading(loadingContent));
    yield put(
      ToastActions.showToast({
        description: `${landingZoneName} Cloud foundation Created`,
        type: NotificationTypes.SUCCESS,
      }),
    );

    yield put(
      BuildDeployActions.checkLandingZoneStatus({
        landingZoneId: landingZoneObj.landingZoneId,
        landingZoneName: landingZoneName,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      const { index } = action.data as IConfigureLandingZoneRequestPayload;
      yield put(BuildDeployActions.configureLandingZoneError({ error, index: index }));
      yield put(
        ToastActions.showToast({
          description: 'Cloud Environment creation failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* configureMicroServicesWorker(action: BuildDeployAction) {
  try {
    const { landingZoneName } = action.data as IConfigureWorkLoadsPattern;
    const microServiceId: string = yield BuildDeployService.uploadMicroservicesYamlFnc(
      action.data as IConfigureWorkLoadsPattern,
    );
    yield put(
      BuildDeployActions.configureMicroServicesLoading({ landingZoneName, microServiceId }),
    );
    yield put(
      ToastActions.showToast({
        description: 'Microservice Creating',
        type: NotificationTypes.SUCCESS,
      }),
    );

    yield put(
      BuildDeployActions.checkMicroServicesPatternStatus({ microServiceId, landingZoneName }),
    );
  } catch (error) {
    if (error instanceof Error) {
      const { landingZoneName } = action.data as IConfigureWorkLoadsPattern;
      yield put(BuildDeployActions.configureMicroServicesPatternError({ error, landingZoneName }));
      yield put(
        ToastActions.showToast({
          description: 'Microservice creation failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* configureEtlWorker(action: BuildDeployAction) {
  try {
    // API call to configure Cloud foundation
    yield put(BuildDeployActions.configureEtlPatternSuccess(action.data as number));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.configureEtlPatternError(error));
    }
  }
}
export function* getArchitectureListWorker(action: BuildDeployAction) {
  try {
    const architectureList: IArchitectureCatalogueResponse[] =
      yield BuildDeployService.getArchitectureListServive();
    yield put(
      BuildDeployActions.getArchitectureListSuccess(
        architectureList as IArchitectureCatalogueResponse[],
      ),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getArchitectureListError(error));
    }
  }
}

export function* checkLandingZoneStatus(action: BuildDeployAction) {
  try {
    const { landingZoneId, landingZoneName } = action.data as ICheckLandingZoneStatus;
    const status: string = yield BuildDeployService.checkLandingZoneStatus(landingZoneId);
    //TODO : Need to get response object structure changed from backend.
    if (status.toLowerCase().includes(LandingZoneStatus.CREATED.toLowerCase())) {
      yield put(BuildDeployActions.configureLandingZoneSuccess(landingZoneName));
      yield put(
        ToastActions.showToast({
          description: `Cloud foundation - ${landingZoneName} created!`,
          type: NotificationTypes.SUCCESS,
        }),
      );
    } else if (status.toLowerCase().includes(LandingZoneStatus.ERROR.toLowerCase())) {
      yield put(BuildDeployActions.configureLandingZoneFailed(landingZoneName));
      yield put(
        ToastActions.showToast({
          description: `Cloud foundation - ${landingZoneName} Error!`,
          type: NotificationTypes.SUCCESS,
        }),
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      Toast('Error in fetching Cloud foundation status', '', NotificationTypes.ERROR);
    }
  }
}

export function* checkMicroServicesPatternStatus(action: BuildDeployAction) {
  try {
    const { microServiceId, landingZoneName } = action.data as ICheckMicroServicesPatternStatus;
    const status: string = yield BuildDeployService.checkMicroServicesPatternStatus(microServiceId);
    //TODO : Need to get response object structure changed from backend.
    if (status.toLowerCase().includes(LzMicroserviceStatus.CREATED.toLowerCase())) {
      yield put(
        BuildDeployActions.configureMicroServicesPatternSuccess({
          microServiceId,
          landingZoneName,
        }),
      );
      yield put(
        ToastActions.showToast({
          description: `Microservice for Cloud foundation - ${landingZoneName} created!`,
          type: NotificationTypes.SUCCESS,
        }),
      );
    } else if (status.toLowerCase().includes(LzMicroserviceStatus.ERROR.toLowerCase())) {
      yield put(BuildDeployActions.configureMicroServicesPatternFailed(landingZoneName));
      yield put(
        ToastActions.showToast({
          description: `Microservice for Cloud foundation - ${landingZoneName} Error!`,
          type: NotificationTypes.ERROR,
        }),
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      Toast('Error in fetching microservice status', '', NotificationTypes.ERROR);
    }
  }
}

export function* getLandingZoneError(action: BuildDeployAction) {
  try {
    const response: ILandingZoneErrorResponse = yield BuildDeployService.getLandingZoneError(
      action.data as string,
    );
    yield put(BuildDeployActions.getLandingZoneErrorSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getLandingZoneErrorFailed(error));
    }
  }
}

export function* createLandingZoneYamlWorker(action: BuildDeployAction) {
  try {
    const { landingZoneName } = action.data as ICreateYamlPayload;
    const response: { body: string } = yield BuildDeployService.createLandingZoneService(
      action.data as ICreateYamlPayload,
    );

    yield put(BuildDeployActions.createLandingZoneYamlSuccess(response.body));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.createLandingZoneYamlError(error));
    }
  }
}
export function* architectureViewCountWorker(action: BuildDeployAction) {
  try {
    yield BuildDeployService.setArchitectureViewCount(action.data as string);
    yield put(BuildDeployActions.onArchitectureViewCountSuccess());
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.onArchitectureViewCountError(error));
    }
  }
}
export function* architectureDiagramWorker(action: BuildDeployAction) {
  try {
    yield BuildDeployService.setArchitectureDiagram(action.data as IArchitectureCataloguePayload);
    yield put(BuildDeployActions.setArchitectureDiagramSuccess());
    yield put(BuildDeployActions.getArchitectureListStarted());
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.onArchitectureViewCountError(error));
    }
  }
}

export function* getEnvironmentDeployedWorker(action: BuildDeployAction) {
  try {
    const response: IEnvironmentsDeployed[] =
      yield BuildDeployService.getEnvironmentsDeployedService(action.data as string);
    yield put(BuildDeployActions.getDeployedEnvironmentsDataSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getDeployedEnvironmentsDataError(error));
    }
  }
}
export function* landingZoneDeploymentStatusWorker(action: BuildDeployAction) {
  try {
    const response: ILandingZoneDeploymentStatus[] =
      yield BuildDeployService.getLandingZoneDeploymentStatusServive(action.data as string);
    yield put(BuildDeployActions.landingZoneDeploymentStatusSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.landingZoneDeploymentStatusError(error));
    }
  }
}
export function* pipelinesAnalyticsWorker(action: BuildDeployAction) {
  try {
    const response: IPipelinesAnalyticsResponse[] =
      yield BuildDeployService.getPipelineAnalyticsService();
    yield put(BuildDeployActions.getPipelinesAnalyticsSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getPipelinesAnalyticsError(error));
    }
  }
}
export function* getYamlContentWorker(action: BuildDeployAction) {
  try {
    const response: IYamlContent = yield BuildDeployService.getYamlContent(action.data as string);
    yield put(BuildDeployActions.getYamlContentSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getYamlContentError(error));
    }
  }
}

export function* getDeafultTemplateYamlContentWorker(action: BuildDeployAction) {
  try {
    const response: IYamlContent = yield BuildDeployService.getDefaultTemplateYamlContent(
      action.data as ICloudLandingZoneKeysPayload,
    );
    yield put(BuildDeployActions.getDefaultTemplateYamlContentSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getDefaultTemplateYamlContentError(error));
    }
  }
}

export function* getDeafultTemplateYamlContentForCloudWorker(action: BuildDeployAction) {
  try {
    const response: IYamlContent = yield BuildDeployService.getDefaultTemplateYamlContentForCloud(
      action.data as ICloudLandingZoneKeysPayload,
    );
    yield put(BuildDeployActions.getDefaultTemplateYamlContentSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getDefaultTemplateYamlContentError(error));
    }
  }
}

export function* getIndustryYamlContentWorker(action: BuildDeployAction) {
  try {
    const response: string = yield BuildDeployService.getIndustryYamlContent(
      action.data as IIndustryYamlPayload,
    );
    yield put(BuildDeployActions.getIndustryYamlContentSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getIndustryYamlContentError(error));
    }
  }
}
export function* pipelinesCummulativeAnalyticsWorker(action: BuildDeployAction) {
  try {
    const response: IPipelinesCummulativeAnalyticsResponse[] =
      yield BuildDeployService.getPipelineCummulativeAnalyticsService();
    yield put(BuildDeployActions.getPipelinesCummulativeAnalyticsSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getPipelinesCummulativeAnalyticsError(error));
    }
  }
}
export function* getCloudLandingZoneKeysWorker(action: BuildDeployAction) {
  try {
    const response: ICloudLandingZoneKeysResponse[] =
      yield BuildDeployService.getCloudLandingZoneKeyservice(action.data as IGetKeysRequestBody);
    yield put(BuildDeployActions.getCloudLandingZoneKeysSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getCloudLandingZoneKeysError(error));
    }
  }
}
export function* setCloudLandingZoneKeysWorker(action: BuildDeployAction) {
  const data = action.data as ICloudLandingZoneKeysPayload;
  const { landingZoneName, cloud } = action.data as ICloudLandingZoneKeysPayload;
  try {
    const response: ILandingZoneState = yield BuildDeployService.setCloudLandingZoneKeyservice(
      data,
    );
    const loadingContent: IConfigureLZLoadingPayload = {
      csp: cloud as CloudServiceProvider,
      landingZoneObj: response,
      index: 0,
      landingZoneName: landingZoneName,
      sector: ILandingZoneSector.GENERAL,
    };
    yield put(BuildDeployActions.configureLandingZoneLoading(loadingContent));
    yield put(
      ToastActions.showToast({
        description: `${landingZoneName} Cloud foundation Creating`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.setCloudLandingZoneKeysError(error));
      yield put(
        ToastActions.showToast({
          description: 'Cloud Environment creation failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}
export function* configureDeployCloudWorker(action: BuildDeployAction) {
  try {
    const response: { instanceId: string } = yield BuildDeployService.configureDeployCloudService(
      action.data as IConfigureDeployCloudPayload,
    );
    yield put(BuildDeployActions.configureDeployCloudSuccess(response.instanceId));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.configureDeployCloudError(error));
    }
  }
}

export function* configureDeployCloudThroughConsoleWorker(action: BuildDeployAction) {
  try {
    const response: { status: string } =
      yield BuildDeployService.configureDeployCloudThroughConsoleService(
        action.data as IConfigureDeployCloudThroughConsolePayload,
      );
    yield put(BuildDeployActions.configureDeployCloudThroughConsoleSuccess(response.status));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.configureDeployCloudThroughConsoleError(error));
    }
  }
}

export function* cloudLandingZoneDeployStatusWorker(action: BuildDeployAction) {
  try {
    const response: ILandingZoneDeploymentResponse =
      yield BuildDeployService.cloudLandingZoneDeployStatusService(action.data as string);
    yield put(BuildDeployActions.cloudLandingZoneDeployStatusSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.cloudLandingZoneDeployStatusError(error));
    }
  }
}

export function* getDeploymentsWorker(action: BuildDeployAction) {
  try {
    //API call to get all deployment work flows
    const { projectId, accountId } = action.data as { projectId: string; accountId: string };
    const deployments: IDeploymentsResponse[] = yield BuildDeployService.getDeploymentsList(
      projectId,
      accountId,
    );

    yield put(BuildDeployActions.getDeploymentDetailsSuccess(deployments));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getDeploymentDetailsError(error));
    }
  }
}

export function* getWorkFLowLogsWorker(action: BuildDeployAction) {
  try {
    //API call to get all work flow logs
    const { landingZoneId, csp } = action.data as {
      landingZoneId: string;
      csp: string;
    };
    const logs: IWorkFLowLogsResponse = yield BuildDeployService.getWorlFlowLogs(
      landingZoneId,
      csp,
    );

    yield put(BuildDeployActions.getWorkFlowLogsSuccess({ ...logs, LandingZoneId: landingZoneId }));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getWorkFlowLogsError(error));
    }
  }
}

export function* getDeploymentAuditsWorker(action: BuildDeployAction) {
  try {
    //API call to get all deployment work flows
    // const { landingZoneId, workflowId } = action.data as IDeploymentLogsPayload;

    const payload = action.data as IDeploymentLogsPayload;
    const { landingZoneId, workflowId } = payload;

    const deployments: IDeploymentLogsResponse[] = yield BuildDeployService.getDeploymentAuditList(
      landingZoneId,
      workflowId,
    );

    yield put(BuildDeployActions.getDeploymentAuditSuccess(deployments));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getDeploymentAuditError(error));
    }
  }
}

export function* cloudLandingZoneDeployDetailsWorker(action: BuildDeployAction) {
  try {
    const { status, landingZoneId, workflowId, instanceId } =
      action.data as IUpdateLandingZoneDeployDetails;
    const response: IDeployActivityHistory = yield BuildDeployService.updateLandingZoneDeployLogs(
      status,
      landingZoneId,
      workflowId,
      instanceId,
    );
    yield put(BuildDeployActions.cloudLandingZoneDeployDetailedLogsSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.cloudLandingZoneDeployDetailedLogsError(error));
    }
  }
}
export function* getMultiCloudYamlContentWorker(action: BuildDeployAction) {
  try {
    const response: Record<string, unknown> = yield BuildDeployService.getMultiCloudYamlContent(
      action.data as IFormInterface,
    );
    yield put(BuildDeployActions.getMultiCloudYamlContentSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getMultiCloudYamlContentError(error));
    }
  }
}

export function* getPreviewYamlArchitectureWorker(action: BuildDeployAction) {
  try {
    // API call to configure Cloud foundation
    const { index, landingZoneName, uploadData, providerName } =
      action.data as IConfigureLandingZonePayload;
    const requestPayload = { index, landingZoneName, uploadData, providerName };
    const data = JSON.parse(uploadData?.userdata.content);
    const response: string = yield BuildDeployService.getPreviewYamlArchitecture(requestPayload);
    yield put(BuildDeployActions.getPreviewYamlArchitectureSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      const { index } = action.data as IConfigureLandingZoneRequestPayload;
      yield put(BuildDeployActions.getPreviewYamlArchitectureError({ error, index: index }));
      // yield put(
      //   ToastActions.showToast({
      //     description: 'Fetching of the Architecture failed! Please close and try again...',
      //     type: NotificationTypes.ERROR,
      //   }),
      // );
    }
  }
}

export function* deployLandingZoneWorker(action: BuildDeployAction) {
  try {
    // API call to deploy Cloud foundation
    const { index, landingZoneName, uploadData, providerName, apiEndpoint } =
      action.data as IConfigureLandingZonePayload;
    const requestPayload = { index, landingZoneName, uploadData, providerName, apiEndpoint };
    const data = JSON.parse(uploadData?.userdata.content);
    const response: ILandingZoneState = yield BuildDeployService.landingZoneDeployStart(
      requestPayload,
    );
    const loadingContent: IConfigureLZLoadingPayload = {
      csp: providerName as CloudServiceProvider,
      landingZoneObj: response,
      index: 0,
      landingZoneName: landingZoneName,
      sector: data.sector,
    };
    yield put(BuildDeployActions.deployLandingZoneLoading(loadingContent));
    yield put(
      ToastActions.showToast({
        description: `${landingZoneName} Cloud foundation Deploying`,
        type: NotificationTypes.SUCCESS,
      }),
    );

    // yield put(
    //   BuildDeployActions.checkLandingZoneStatus({
    //     landingZoneId: response.landingZoneId,
    //     landingZoneName: landingZoneName,
    //   }),
    // );
  } catch (error) {
    if (error instanceof Error) {
      const { index } = action.data as IConfigureLandingZoneRequestPayload;
      yield put(BuildDeployActions.deployLandingZoneError({ error, index: index }));
      yield put(
        ToastActions.showToast({
          description: 'Cloud Environment creation failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}
export function* createEnvironmentWorker(action: BuildDeployAction) {
  try {
    // API call to deploy Cloud foundation
    const uploadData = action.data as ICreateEnvironmentPayload;
    const response: IAllEnvironmentsRespObj = yield BuildDeployService.createEnvironment(
      uploadData,
    );

    yield put(BuildDeployActions.createEnvironmentSuccess(response));
    yield put(
      BuildDeployActions.getAllEnvironmentsStarted({
        account: uploadData.userdata.account,
        project: uploadData.userdata.project,
      }),
    );
    yield put(
      ToastActions.showToast({
        description: `${response.environmentName} Environemnt Created`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.createEnvironmentError(error));
      yield put(
        ToastActions.showToast({
          description: 'Cloud Environment creation failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* validateYamlPollWorker(logId: string, retryCount = 0): any {
  try {
    const response: string = yield BuildDeployService.validatePollYaml(logId);
    yield put(
      BuildDeployActions.validateYamlSuccess({
        logs: response.toString(),
        planId: logId.toString(),
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      if (retryCount === 10) {
        yield put(BuildDeployActions.validateYamlError(error));
        yield put(
          ToastActions.showToast({
            description: 'Validating Yaml Failed',
            type: NotificationTypes.ERROR,
          }),
        );
      } else {
        yield validateYamlPollWorker(logId, retryCount + 1);
      }
    }
  }
}

export function* validateYamlForCloudPollWorker(logId: string, retryCount = 0): any {
  try {
    const response: string = yield BuildDeployService.validateForCloudPollYaml(logId);
    yield put(
      BuildDeployActions.validateYamlSuccess({
        logs: response.toString(),
        planId: logId.toString(),
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      if (retryCount === 10) {
        yield put(BuildDeployActions.validateYamlError(error));
        yield put(
          ToastActions.showToast({
            description: 'Validating Yaml Failed',
            type: NotificationTypes.ERROR,
          }),
        );
      } else {
        yield validateYamlForCloudPollWorker(logId, retryCount + 1);
      }
    }
  }
}

export function* validateYamlWorker(action: BuildDeployAction, retryCount = 0): any {
  try {
    const response: string = yield BuildDeployService.validateYaml(action.data as IUploadPayload);
    yield delay(20000);
    yield validateYamlPollWorker(response);
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.validateYamlError(error));
      yield put(
        ToastActions.showToast({
          description: 'Validating Yaml Failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* validateCustomTemplatePollWorker(validateCustomConfigResponse: IValidateCustomConfigurationResponse, retryCount = 0): any {
  try {
    console.log('landingZoneId', validateCustomConfigResponse.landingZoneId);
    const response: string = yield BuildDeployService.validateCustomPollYaml(validateCustomConfigResponse.planId);
    yield put(
      BuildDeployActions.validateYamlSuccess({
        logs: response.toString(),
        planId: validateCustomConfigResponse.planId.toString(),
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      if (retryCount === 10) {
        yield put(BuildDeployActions.validateCustomTemplateError(error));
        yield put(
          ToastActions.showToast({
            description: 'Validating Yaml Failed',
            type: NotificationTypes.ERROR,
          }),
        );
      } else {
        yield validateCustomTemplatePollWorker(validateCustomConfigResponse, retryCount + 1);
      }
    }
  }
}

export function* validateCustomTemplateZipFile(action: BuildDeployAction, retryCount = 0): any {
  try{
    const response: IValidateCustomConfigurationResponse = yield BuildDeployService.validateCustomTemplateZipFile(action.data as IValidateCustomConfigurationPayload);

    yield delay(20000);
    yield validateCustomTemplatePollWorker(response);
  } catch (error) {
    if (error instanceof Error) {
      ;
    }
  }
}

export function* validateLandingZoneWorker(action: BuildDeployAction, retryCount = 0): any {
  try {
    const response: string = yield BuildDeployService.validateLandingZone(
      action.data as IValidateLandingZonePayload,
    );
    yield put(BuildDeployActions.validateLandingZoneSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.validateYamlError(error));
      yield put(
        ToastActions.showToast({
          description: 'Validating Landing Zone Failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* validateYamlForCloudWorker(action: BuildDeployAction, retryCount = 0): any {
  try {
    const response: string = yield BuildDeployService.validateForCloudYaml(
      action.data as IUploadPayload,
    );
    yield delay(20000);
    yield validateYamlForCloudPollWorker(response);
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.validateYamlError(error));
      yield put(
        ToastActions.showToast({
          description: 'Validating Yaml Failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* fetchIndustryCompliancesWorker(action: BuildDeployAction) {
  try {
    const response: Array<IIndustryCompliance> =
      yield BuildDeployService.fetchIndustryCompliances();
    yield put(BuildDeployActions.fetchIndustryCompliancesSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.fetchIndustryCompliancesError(error));
      yield put(
        ToastActions.showToast({
          description: error.message,
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* fetchIndustryPoliciesWorker(action: BuildDeployAction) {
  try {
    const response: Array<IIndustryPolicy> = yield BuildDeployService.fetchIndustryPolicies(
      action.data as IIndustryPolicyRequest,
    );
    yield put(BuildDeployActions.fetchIndustryPoliciesSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.fetchIndustryPoliciesError(error));
      yield put(
        ToastActions.showToast({
          description: error.message,
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* fetchIndustryPoliciesForCloudWorker(action: BuildDeployAction) {
  try {
    const response: Array<IIndustryPolicy> = yield BuildDeployService.fetchIndustryPoliciesForCloud(
      action.data as IIndustryPolicyRequest,
    );
    yield put(BuildDeployActions.fetchIndustryPoliciesSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.fetchIndustryPoliciesError(error));
      yield put(
        ToastActions.showToast({
          description: error.message,
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* downloadArchitectureDiagramWorker(action: BuildDeployAction) {
  try {
    const response: { download_url: string } = yield BuildDeployService.downloadArchitectureDiagram(
      action.data as IDownloadArchitectureDiagramPayload,
    );
    yield put(BuildDeployActions.downloadArchitectureDiagramSuccess(response.download_url));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.downloadArchitectureDiagramError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to download the Architecture Diagram',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* fetchCategoryAndTechDomainWorker() {
  try {
    const response: IFetchCategoryAndTechDomainResponse =
      yield BuildDeployService.fetchCategoryAndTechDomain();
    yield put(BuildDeployActions.getCategoryAndTechDomainSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getCategoryAndTechDomainError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to fetch Category and Tech Domain values',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* uploadArchitectureWorker(action: BuildDeployAction) {
  try {
    yield BuildDeployService.uploadArchitecture(action.data as IUploadArchitecturePayload);
    yield put(BuildDeployActions.uploadArchitectureSuccess());
    yield put(
      ToastActions.showToast({
        description: `Submitted ${
          (action.data as IUploadArchitecturePayload).architectureName
        } successfulley`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.uploadArchitectureError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to fetch Category and Tech Domain values',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* fetchIndustryPoliciesYamlWorker(action: BuildDeployAction) {
  try {
    const response: {
      [key: string]: string;
    } = yield BuildDeployService.fetchIndustryPoliciesYaml(action.data as Array<string>);
    yield put(BuildDeployActions.getIndustryPolicyYamlSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getIndustryPolicyYamlError(error));








    }
  }
}

export function* fetchTerraformCodeWorker(action: BuildDeployAction) {
  try {
    const response: string = yield BuildDeployService.fetchTerraformCode(
      action.data as IUploadPayload,
    );
    yield put(BuildDeployActions.getTerraformCodeSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getTerraformCodeError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to fetch Terraform Code',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* fetchTerraformCodeForCloudWorker(action: BuildDeployAction) {
  try {
    const response: string = yield BuildDeployService.fetchTerraformCodeForCloud(
      action.data as IUploadPayload,
    );
    yield put(BuildDeployActions.getTerraformCodeSuccess(response));
    yield put(
      ToastActions.showToast({
        description: `Terraform modules downloaded successfully`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getTerraformCodeError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to fetch Terraform Code',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* getGenAiCreateServicesWorker(action: BuildDeployAction) {
  try {
    const response: Record<string, Array<string>> = yield BuildDeployService.getGenAiCreateServices(
      action.data as IGetKeysRequestBody,
    );
    yield put(BuildDeployActions.getGenAiCreateServicesSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getGenAiCreateServicesError(error));
      yield put(
        ToastActions.showToast({
          description: 'Fetching Services list failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* genAiCreatePollWorker(payload: IGenAiCreateDownloadPayload, retryCount = 0): any {
  try {
    const response: any = yield BuildDeployService.getGenAiCreatePollCompletion(payload);
    yield put(BuildDeployActions.getGenAiCreatePollSuccess(response));
    yield put(
      ToastActions.showToast({
        description: 'Terraform Code was created Successfully',
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      if (retryCount === 10) {
        yield put(BuildDeployActions.getGenAiCreatePollError(error));
        yield put(
          ToastActions.showToast({
            description: 'Creating Terraform Code Failed',
            type: NotificationTypes.ERROR,
          }),
        );
      } else {
        yield delay(20000);
        yield genAiCreatePollWorker(payload, retryCount + 1);
      }
    }
  }
}

export function* getGenAiCreateYamlContentWorker(action: BuildDeployAction) {
  try {
    const response: IGenAiCreateResponse =
      yield BuildDeployService.getGenAiCreateYamlContent(action.data as IGenAiCreatePayload);
    const correctedResponse = {
      terraformFolderName: response.terraformFolderName,
    };
    const payload: IGenAiCreateDownloadPayload = {
      services: (action.data as IGenAiCreatePayload).services,
      ...correctedResponse,
    };
    yield put(BuildDeployActions.getGenAiCreateYamlContentSuccess(correctedResponse));
    // yield delay(5000);
    // yield genAiCreatePollWorker(payload);
  } catch (error) {
    console.log('error', error);
    if (error instanceof Error) {
      yield put(BuildDeployActions.getGenAiCreateYamlContentError(error));
      yield put(
        ToastActions.showToast({
          description: 'Creation of Terraform Code Failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* getGenAiCreateDownloadWorker(action: BuildDeployAction) {
  try {
    const response: string = yield BuildDeployService.getGenAiCreateDownload(
      action.data as IGenAiCreateDownloadPayload,
    );
    yield put(BuildDeployActions.getGenAiCreateDownloadSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getGenAiCreateDownloadError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to fetch Terraform Code',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* generateArchitectureDiagramsWorker(action: BuildDeployAction) {
  try {
    const payload = action.data as IGenerateArchitectureDiagramsPayload;
    const response: IGenerateArchitectureDiagramsResponse = yield BuildDeployService.generateArchitectureDiagram(
      action.data as IGenerateArchitectureDiagramsPayload,
    );

    yield put(BuildDeployActions.generateArchitectureDiagramsSuccess(response, payload.diagramType));
  } catch (error) {
    if (error instanceof Error) {
      console.log(error)
      yield put(BuildDeployActions.generateArchitectureDiagramsError());
      yield put(
        ToastActions.showToast({
          description: 'Failed to generate Architecture Diagrams',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* generateArchitectureDiagramsRevisionWorker(action: BuildDeployAction) {
  try {
    const payload = action.data as IReviseArchitectureDiagramsPayload;
    const response: IGenerateArchitectureDiagramsResponse = yield BuildDeployService.generateArchitectureDiagramsRevision(
      payload,
    );
    yield put(BuildDeployActions.generateArchitectureDiagramsRevisionSuccess(response));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.reviseGeneratedArchitectureDiagramError())
      yield put(
        ToastActions.showToast({
          description: 'Failed to revise Architecture Diagrams',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* generateArchitectureDiagramsConversionWorker(action: BuildDeployAction) {
  try {
    const payload = action.data as IConvertArchitectureDiagramsPayload;
    const response: IGenerateArchitectureDiagramsResponse = yield BuildDeployService.generateArchitectureDiagramsConversion(
      payload,
    );
    yield put(BuildDeployActions.generateArchitectureDiagramsConversionSuccess(response,payload.diagramType));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.reviseGeneratedArchitectureDiagramError())
      yield put(
        ToastActions.showToast({
          description: 'Failed to convert Architecture Diagrams',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* uploadGeneratedArchitectureDiagramWorker(action: BuildDeployAction) {
  try {
    const payload = action.data as IUploadArchitectureDiagramsPayloadWithFile;
    let response: IUploadArchitectureDiagramsResponse;
    if (payload.file) {
      response = yield BuildDeployService.uploadGeneratedArchitectureDiagram(
      payload.data as IUploadArchitectureDiagramsPayload,
      payload.file as File,
      );
    } else {
      response = yield BuildDeployService.uploadGeneratedArchitectureDiagram(
      payload.data as IUploadArchitectureDiagramsPayload,
      );
    }
    yield put(BuildDeployActions.uploadGeneratedArchitectureDiagramSuccess(response));
    yield put(
      ToastActions.showToast({
        description: 'Uploaded Architecture Diagram successfully',
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(
        ToastActions.showToast({
          description: 'Failed to upload Architecture Diagram',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* uploadCustomTemplateZipFIle(action: BuildDeployAction) {
  try{
    const response: IUploadCustomCloudConfigurationFileResponse = yield BuildDeployService.uploadCustomTemplateZipFle(
      action.data as IUploadCustomCloudConfigurationFilePayload
    )
    console.log('response', response);
    yield put(BuildDeployActions.uploadCustomTemplateDelpoymentZipFileSuccess(response))
    yield put(
        ToastActions.showToast({
        description: `${(action.data as IUploadCustomCloudConfigurationFilePayload).zip_file.name} uploaded successfully`,
        type: NotificationTypes.SUCCESS,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.uploadCustomTemplateDelpoymentZipFileError(error));
      yield put(
        ToastActions.showToast({
          description: 'Failed to upload zip file',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* getCloudBuildConfigVariables(action: BuildDeployAction) {
  try {
    // Step 1: Fetch config vars
    const response: ITemporaryCloudBuildConfigVariableResponse = yield BuildDeployService.getCloudBuildConfigVariables(
      action.data as ICloudBuildTfVarsPayload,
    );
    yield put(BuildDeployActions.getEnvironmentBuildCloudConfigurationVariablesSuccess(response.variables));
  } catch (error) {
    if (error instanceof Error) {
      yield put(BuildDeployActions.getEnvironmentBuildCloudConfigurationVariablesError(error));
      yield put(
        ToastActions.showToast({
          description: error.message,
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* configureCustomLandingZoneWorker(action: BuildDeployAction) {
  try {
    const { index, landingZoneName, uploadData, providerName, apiEndpoint, projectId } =
      action.data as IConfigureLandingZonePayload;
    const requestPayload = { index, landingZoneName, uploadData, providerName, apiEndpoint };
    const data = JSON.parse(uploadData?.userdata.content);
    const response: ILandingZoneResponse = yield BuildDeployService.startCustomLandingZoneUpload(requestPayload);

    const {
      LandingZoneName,
      CurrentStatus,
      CloudServiceProvider: cloudServiceProvider,
      LandingZoneId,
      Sector: sector,
      GCPProjectId,
      MicroserviceInfo,
      Errors,
      IAC,
      CreatedOn,
      industryComplianceId,
      industryComplianceName,
      complianceStandardList,
      AccountId,
      ProjectId,
    } = response;

    const isEnabled = true;
    const microServicesConfigured = MicroserviceInfo?.status === LzMicroserviceStatus.CREATED;
    const microServicesError = MicroserviceInfo?.status === LzMicroserviceStatus.ERROR;
    const landingZoneObj: ILandingZoneState = {
      landingZoneId: LandingZoneId,
      landingZoneEnabled: isEnabled,
      landingZoneConfigured: isEnabled,
      landingZoneName: LandingZoneName,
      projectName: GCPProjectId,
      loadingLandingZone: !isEnabled,
      loadingEtlWorkload: false,
      sector: sector,
      loadingMicroServicesWorkload: Boolean(
        isEnabled && MicroserviceInfo?.ids && !microServicesConfigured && !microServicesError,
      ),
      workLoadPatternMicroServicesEnabled: microServicesConfigured,
      workLoadPatternEtlEnabled: false,
      workLoadPatternMicroServicesConfigured: microServicesConfigured,
      workLoadPatternMicroServicesError: microServicesError,
      workLoadPatternEtlConfigured: false,
      cloudServiceProvider: cloudServiceProvider,
      workLoadPatternMicroServicesId: MicroserviceInfo?.ids,
      landingZoneStatus:
        CurrentStatus === LandingZoneStatus.IN_PROGRESS ? LandingZoneStatus.CREATED : CurrentStatus,
      errorMessage: Errors,
      IAC,
      createdOn: CreatedOn,
      accountId: AccountId,
      projectId: ProjectId,
      industryComplianceId,
      industryComplianceName,
      complianceStandardList,
    };

    const loadingContent: IConfigureLZLoadingPayload = {
      csp: providerName as CloudServiceProvider,
      landingZoneObj: landingZoneObj,
      index: 0,
      landingZoneName: landingZoneName,
      sector: data.sector,
    };

    yield put(BuildDeployActions.configureLandingZoneLoading(loadingContent));
    yield put(
      ToastActions.showToast({
        description: `${landingZoneName} Custom Cloud foundation Created`,
        type: NotificationTypes.SUCCESS,
      }),
    );

    yield put(
      BuildDeployActions.checkLandingZoneStatus({
        landingZoneId: landingZoneObj.landingZoneId,
        landingZoneName: landingZoneName,
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      const { index } = action.data as IConfigureLandingZoneRequestPayload;
      yield put(BuildDeployActions.configureCustomLandingZoneError({ error, index: index }));
      yield put(
        ToastActions.showToast({
          description: 'Custom Landing Zone creation failed',
          type: NotificationTypes.ERROR,
        }),
      );
    }
  }
}

export function* hardReloadWorker() {
  yield put(BuildDeployActions.handleHardReload());
}

export function* buildDeployWatcher() {
  yield takeEvery(
    BuildDeployActionTypes.CONFIGURE_LANDING_ZONE_STARTED,
    configureLandingZoneWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.CONFIGURE_CUSTOM_LANDING_ZONE_STARTED,
    configureCustomLandingZoneWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.ENABLE_MICROSERVIVES_PATTERN_STARTED,
    enableMicroServicesWorkload,
  );
  yield takeEvery(BuildDeployActionTypes.ENABLE_LANDING_ZONE_STARTED, enableLandingZoneWorker);
  yield takeEvery(BuildDeployActionTypes.DELETE_LANDING_ZONE_STARTED, destroyLandingZoneWorker);
  yield takeEvery(
    BuildDeployActionTypes.CONFIGURE_MICROSERVIVES_PATTERN_STARTED,
    configureMicroServicesWorker,
  );
  yield takeEvery(BuildDeployActionTypes.CONFIGURE_ETL_PATTERN_STARTED, configureEtlWorker);
  yield takeEvery(BuildDeployActionTypes.ENABLE_ETL_PATTERN_STARTED, enableEtlWorkLoadWorker);
  yield takeEvery(BuildDeployActionTypes.GET_BUILD_DEPLOY_DETAILS_STARTED, getLandingZonesWorker);
  yield takeEvery(BuildDeployActionTypes.CHECK_LANDING_ZONE_STATUS, checkLandingZoneStatus);
  yield takeEvery(
    BuildDeployActionTypes.CHECK_MICROSERVIVES_PATTERN_STATUS,
    checkMicroServicesPatternStatus,
  );
  yield takeEvery(
    BuildDeployActionTypes.CONFIGURE_LANDING_ERROR_SUCCESS,
    configureLandingZoneWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.DESTROY_WORKLOAD_PATTERN_STARTED,
    destroyWorkLoadPatternWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_ALL_AVAILABLE_WORKLOAD_PATTERN_STARTED,
    getWorkLoadPatternWorker,
  );
  yield takeEvery(BuildDeployActionTypes.GET_LANDING_ZONE_ERRORS_STARTED, getLandingZoneError);
  yield takeEvery(
    BuildDeployActionTypes.CREATE_LANDING_ZONE_YAML_START,
    createLandingZoneYamlWorker,
  );
  yield takeEvery(BuildDeployActionTypes.ENVIRONMENTS_DEPLOYED_START, getEnvironmentDeployedWorker);
  yield takeEvery(
    BuildDeployActionTypes.LANDING_ZONES_DEPLOYMENT_STATUS_START,
    landingZoneDeploymentStatusWorker,
  );
  yield takeEvery(BuildDeployActionTypes.GET_ARCHITECTURE_LIST_STARTED, getArchitectureListWorker);
  yield takeEvery(
    BuildDeployActionTypes.SET_ARCHTECTURE_VIEW_COUNT_STARTED,
    architectureViewCountWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.SET_ARCHTECTURE_DIAGRAM_STARTED,
    architectureDiagramWorker,
  );
  yield takeEvery(BuildDeployActionTypes.GET_PIPELINES_ANALYTICS_STARTED, pipelinesAnalyticsWorker);
  yield takeEvery(
    BuildDeployActionTypes.GET_PIPELINES_CUMMULATIVE_ANALYTICS_STARTED,
    pipelinesCummulativeAnalyticsWorker,
  );
  yield takeEvery(BuildDeployActionTypes.GET_YAML_CONTENT_STARTED, getYamlContentWorker);
  yield takeEvery(
    BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_CONTENT_STARTED,
    getDeafultTemplateYamlContentWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_CONTENT_FOR_CLOUD_STARTED,
    getDeafultTemplateYamlContentForCloudWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_INDUSTRY_YAML_CONTENT_STARTED,
    getIndustryYamlContentWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_CLOUD_LANDING_ZONE_KEYS_STARTED,
    getCloudLandingZoneKeysWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.SET_CLOUD_LANDING_ZONE_KEYS_STARTED,
    setCloudLandingZoneKeysWorker,
  );
  yield takeEvery(BuildDeployActionTypes.GET_DEPLOYMENT_DETAILS_STARTED, getDeploymentsWorker);
  yield takeEvery(
    BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_STARTED,
    configureDeployCloudWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.CONFIGURE_DEPLOY_CLOUD_THROUGH_CONSOLE_STARTED,
    configureDeployCloudThroughConsoleWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_STATUS_STARTED,
    cloudLandingZoneDeployStatusWorker,
  );
  yield takeEvery(BuildDeployActionTypes.GET_DEPLOYMENT_AUDIT_STARTED, getDeploymentAuditsWorker);
  yield takeEvery(
    BuildDeployActionTypes.CLOUD_LANDING_ZONE_DEPLOY_DETAILED_LOGS_STARTED,
    cloudLandingZoneDeployDetailsWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_MULTICLOUD_YAML_CONTENT_STARTED,
    getMultiCloudYamlContentWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_DEFAULT_TEMPLATE_YAML_ARCH_DIAGRAM_STARTED,
    getPreviewYamlArchitectureWorker,
  );
  yield takeEvery(BuildDeployActionTypes.DEPLOY_LANDING_ZONE_STARTED, deployLandingZoneWorker);
  yield takeEvery(BuildDeployActionTypes.GET_ALL_ENVIRONMENTS_STARTED, getAllEnvironmentsWorker);
  yield takeEvery(BuildDeployActionTypes.CREATE_ENVIRONMENT_STARTED, createEnvironmentWorker);
  yield takeEvery(BuildDeployActionTypes.VALIDATE_YAML_STARTED, validateYamlWorker);
  yield takeEvery(BuildDeployActionTypes.VALIDATE_LANDING_ZONE_STARTED, validateLandingZoneWorker);
  yield takeEvery(BuildDeployActionTypes.VALIDATE_CUSTOM_TEMPLATE_ZIP_FILE_STARTED, validateCustomTemplateZipFile);
  yield takeEvery(
    BuildDeployActionTypes.VALIDATE_YAML_FOR_CLOUD_STARTED,
    validateYamlForCloudWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.FETCH_INDUSTRY_POLICIES_STARTED,
    fetchIndustryPoliciesWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.FETCH_INDUSTRY_POLICIES_FOR_CLOUD_STARTED,
    fetchIndustryPoliciesForCloudWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.FETCH_INDUSTRY_COMPLIANCES_STARTED,
    fetchIndustryCompliancesWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.DOWNLOAD_ARCHITECTURE_DIAGRAM_START,
    downloadArchitectureDiagramWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.FETCH_CATEGORY_AND_TECHDOMAIN_OPTIONS_START,
    fetchCategoryAndTechDomainWorker,
  );
  yield takeEvery(BuildDeployActionTypes.UPLOAD_ARCHITECTURE_START, uploadArchitectureWorker);
  yield takeEvery(
    BuildDeployActionTypes.FETCH_INDUSTRY_POLICY_YAML_START,
    fetchIndustryPoliciesYamlWorker,
  );
  yield takeEvery(BuildDeployActionTypes.FETCH_TERRAFORM_CODE_START, fetchTerraformCodeWorker);
  yield takeEvery(
    BuildDeployActionTypes.FETCH_TERRAFORM_CODE_FOR_CLOUD_START,
    fetchTerraformCodeForCloudWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_GEN_AI_CREATE_SERVICES_STARTED,
    getGenAiCreateServicesWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_GEN_AI_CREATE_YAML_CONTENT_STARTED,
    getGenAiCreateYamlContentWorker,
  );
  yield takeEvery(
    BuildDeployActionTypes.GET_GEN_AI_CREATE_DOWNLOAD_STARTED,
    getGenAiCreateDownloadWorker,
  );

  yield takeEvery(
    BuildDeployActionTypes.UPLOAD_CUSTOM_TEMPLATE_ZIP_FILE_STARTED,
    uploadCustomTemplateZipFIle
  )
  yield takeEvery(BuildDeployActionTypes.GET_WORKFLOW_LOGS_STARTED, getWorkFLowLogsWorker);
  // yield takeEvery(BuildDeployActionTypes.GET_CLOUD_BUILD_CONFIG_VARIABLES_STARTED, getCloudBuildConfigVariables);
  yield takeEvery(BuildDeployActionTypes.GET_CLOUD_BUILD_CONFIG_VARIABLES_STARTED, getCloudBuildConfigVariables);
  yield takeEvery(BuildDeployActionTypes.GENERATE_ARCHITECTURE_DIAGRAMS_STARTED, generateArchitectureDiagramsWorker);
  yield takeEvery(BuildDeployActionTypes.REVISE_GENERATED_ARCHITECTURE_DIAGRAMS_STARTED, generateArchitectureDiagramsRevisionWorker);
  yield takeEvery(BuildDeployActionTypes.CONVERT_GENERATED_ARCHITECTURE_DIAGRAMS_STARTED, generateArchitectureDiagramsConversionWorker);
  yield takeEvery(BuildDeployActionTypes.UPLOAD_GENERATED_ARCHITECTURE_DIAGRAM_STARTED, uploadGeneratedArchitectureDiagramWorker);
  yield takeEvery('persist/REHYDRATE', hardReloadWorker);
}
